import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta


// Función para crear un anuncio
const createAd = async (adData: {
    type: 'premium' | 'regular' | 'newsletter' | 'video' | 'banner' | 'popup';
    title: string;
    userId: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    imageUrl?: string;
    videoUrl?: string;
    category: 'technology' | 'fashion' | 'health' | 'education' | 'entertainment' | 'sports';
    priority?: number;
    isActive?: boolean;
    startDate?: Date;
    endDate?: Date;
  }) => {
    const response = await axiosInstance.post('/ads/create', adData);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para crear un anuncio
  export const useCreateAd = () => {
    const mutation = useMutation({
      mutationFn: createAd, // Función para crear el anuncio
      onSuccess: () => {
        toast.success('Anuncio creado con éxito.');
      },
      onError: (error: any) => {
        // Verifica la existencia de un mensaje de error en la respuesta
        const errorMessage = error?.response?.data?.message || 'Error desconocido';
        toast.error(`Error al crear el anuncio: ${errorMessage}`);
        console.error('Error al crear el anuncio:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // isPending es la propiedad correcta para mutaciones
      isSuccess: mutation.isSuccess,
      error: mutation.error?.response?.data?.message || mutation.error?.message, // Asegúrate de capturar el error correctamente
    };
  };
  

// Función para obtener todos los anuncios
const fetchAllAds = async (page: number, limit: number) => {
  const response = await axiosInstance.get(`/ads/ads?page=${page}&limit=${limit}`);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar todos los anuncios
export const useGetAllAds = (page: number = 1, limit: number = 10) => {
  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ['ads', page, limit], // Identifica la consulta en React Query
    queryFn: async () => {
      try {
        return await fetchAllAds(page, limit);
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
        toast.error(`Error al obtener los anuncios: ${errorMessage}`);
        console.error('Error al obtener los anuncios:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,  // No refetch al cambiar de pestaña
    refetchOnMount: true,         // Hace refetch al montar el componente
    refetchInterval: 120000,      // Refresca los datos cada 2 minutos (120000ms)
  });

  return {
    ads: data?.ads || [],         // Lista de anuncios obtenidos
    pagination: data?.pagination || {}, // Datos de paginación
    isLoading,   
    isError,     
    error,       
    refetch,    // Retorna la función refetch para poder llamarla manualmente si es necesario
  };
};


  // Función para obtener los anuncios de un usuario
const fetchUserAds = async () => {
    const response = await axiosInstance.get('/ads/user-ads');
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para listar los anuncios de un usuario
  export const useGetUserAds = () => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['user-ads'], // Identifica la consulta en React Query
      queryFn: async () => {
        try {
          return await fetchUserAds();
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
          toast.error(`Error al obtener los anuncios del usuario: ${errorMessage}`);
          console.error('Error al obtener los anuncios del usuario:', error);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Evita que los datos se vuelvan a obtener al cambiar de ventana
    });
  
    return {
      userAds: data,   // Lista de anuncios del usuario obtenidos
      isLoading,       // Estado de carga
      isError,         // Indica si hubo un error
      error,           // Detalle del error (si existe)
    };
  };

  

  // Función para eliminar un anuncio
const deleteAd = async (adId: string) => {
    const response = await axiosInstance.delete(`/ads/delete/${adId}`);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para eliminar un anuncio
  export const useDeleteAd = () => {
    const mutation = useMutation({
      mutationFn: deleteAd, // Función para eliminar el anuncio
      onSuccess: () => {
        toast.success('Anuncio eliminado con éxito.');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || 'Error desconocido';
        toast.error(`Error al eliminar el anuncio: ${errorMessage}`);
        console.error('Error al eliminar el anuncio:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
    };
  };

  
  // Función para actualizar un anuncio
const updateAd = async ({ adId, adData }: { adId: string; adData: any }) => {
    const response = await axiosInstance.put(`/ads/update/${adId}`, adData);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para actualizar un anuncio
  export const useUpdateAd = () => {
    const mutation = useMutation({
      mutationFn: updateAd, // Función para actualizar el anuncio
      onSuccess: () => {
        toast.success('Anuncio actualizado con éxito.');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || 'Error desconocido';
        toast.error(`Error al actualizar el anuncio: ${errorMessage}`);
        console.error('Error al actualizar el anuncio:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
    };
  };
  